import type React from "react";
import { DropZone, FileTrigger } from "react-aria-components";
import { useTranslation } from "react-i18next";
import { Button } from "../basic/Button";
import { Flex, VStack } from "../../../../styled-system/jsx";
import { css } from "../../../../styled-system/css";
import Loading from "./Loading";

interface FileDropZoneProps {
  onFileSelect: (file: File) => void;
  label?: string;
  disabled?: boolean;
  isPending?: boolean;
  /** MIME types accepted by the file picker and drop handler (e.g. ["text/csv", ".csv"]) */
  acceptedFileTypes?: string[];
}

const FileDropZone: React.FC<FileDropZoneProps> = ({
  onFileSelect,
  label,
  disabled = false,
  isPending = false,
  acceptedFileTypes,
}) => {
  const { t } = useTranslation(["common"]);
  const resolvedLabel = label ?? t(($) => $.common.fileDropZone["Drop your file here or click the button below"]);
  // RAC doesn't export DropEvent type even though it's what's referenced in the docs for onDrop
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any
  const handleDrop = async (e: any) => {
    if (disabled) return;
    const item = e.items?.[0];
    if (!item) return;
    if (item.kind === "file") {
      const file = await item.getFile();
      if (
        acceptedFileTypes &&
        !acceptedFileTypes.some((t) =>
          t.startsWith(".") ? file.name.endsWith(t)
          : t.endsWith("/*") ? file.type.startsWith(t.slice(0, -1))
          : file.type === t,
        )
      )
        return;
      onFileSelect(file);
    }
  };

  const handleSelect = (files: FileList | undefined | null) => {
    if (disabled || !files?.[0]) return;
    onFileSelect(files[0]);
  };

  return (
    <Flex
      className={css({
        position: "relative",
        justifyContent: "center",
        alignItems: "center",
        border: "1px dashed darkgray",
        borderRadius: "10px",
        width: "100%",
        height: "150px",
        paddingY: "large",
        paddingX: "xlarge",
        backgroundColor: "gray.100",
      })}
    >
      {isPending ?
        <VStack gap={"medium"}>
          <Loading fullPage={false} height={"100px"} backgroundColor={"inherit"} />
          <p className={css({ fontSize: "small" })}>{t(($) => $.common.fileDropZone["Processing your file..."])}</p>
        </VStack>
      : <DropZone
          onDrop={handleDrop}
          className={css({
            cursor: disabled ? "not-allowed" : "pointer",
            display: "flex",
            gap: "medium",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            height: "100%",
            opacity: disabled ? 0.6 : 1,
          })}
        >
          <span>{resolvedLabel}</span>
          <FileTrigger onSelect={handleSelect} acceptedFileTypes={acceptedFileTypes}>
            <Button isDisabled={disabled}>{t(($) => $.common.fileDropZone["Upload File"])}</Button>
          </FileTrigger>
        </DropZone>
      }
    </Flex>
  );
};

export default FileDropZone;
